var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import axios, { AxiosHeaders, } from 'axios';
import { https } from 'follow-redirects';
import { RestClient, } from './RestClient';
import { RestClientError } from './RestClientError';
const errorHandler = ({ err, onError, onCustomError, skipErrorHandling = false, }) => {
    const error = axios.isAxiosError(err) ? new RestClientError(err) : err;
    !skipErrorHandling && (onError === null || onError === void 0 ? void 0 : onError(error, onCustomError));
    return Promise.reject(error);
};
function getHeaders(headers, config) {
    const disableHeaders = config.disableHeaders;
    if (disableHeaders === null || disableHeaders === void 0 ? void 0 : disableHeaders.length) {
        return Object.entries(headers).reduce((result, [name, value]) => {
            if (!disableHeaders.includes(name)) {
                result[name] = value;
            }
            return result;
        }, {});
    }
    return headers;
}
export class RestClientBuilder {
    constructor(defaultRestClientConfig = {}) {
        this._defaultRestClientConfig = {};
        this._customRestClientConfig = {};
        this.authorizationToken = undefined;
        this._defaultRestClientConfig = defaultRestClientConfig;
    }
    setCustomConfig(customRestClientConfig) {
        this._customRestClientConfig = customRestClientConfig;
    }
    createInstance(config) {
        const abortControllersMap = new Map();
        const _a = this._defaultRestClientConfig, { getRequiredHeaders, onError, onResponse } = _a, defaultConfig = __rest(_a, ["getRequiredHeaders", "onError", "onResponse"]);
        const _b = this._customRestClientConfig, { getRequiredHeaders: getCustomRequiredHeaders, onError: onCustomError, onResponse: onCustomResponse } = _b, customConfig = __rest(_b, ["getRequiredHeaders", "onError", "onResponse"]);
        const _c = config || {}, { skipErrorHandling, responseType, rejectUnauthorized = true } = _c, cfg = __rest(_c, ["skipErrorHandling", "responseType", "rejectUnauthorized"]);
        const headers = Object.assign(Object.assign(Object.assign({}, defaultConfig.headers), customConfig.headers), cfg.headers);
        const transformResponse = (config === null || config === void 0 ? void 0 : config.transformResponse) || (customConfig === null || customConfig === void 0 ? void 0 : customConfig.transformResponse) || defaultConfig.transformResponse;
        const transformRequest = (config === null || config === void 0 ? void 0 : config.transformRequest) || (customConfig === null || customConfig === void 0 ? void 0 : customConfig.transformRequest) || defaultConfig.transformRequest;
        const instanceConfig = Object.assign(Object.assign(Object.assign(Object.assign({}, defaultConfig), customConfig), cfg), { headers,
            responseType,
            transformResponse,
            transformRequest });
        const requester = axios.create(Object.assign(Object.assign({}, instanceConfig), { httpsAgent: rejectUnauthorized ? undefined : new https.Agent({ rejectUnauthorized: false }) }));
        requester.interceptors.request.use((requestConfig) => __awaiter(this, void 0, void 0, function* () {
            const reqHeaders = new AxiosHeaders(getHeaders(Object.assign(Object.assign(Object.assign({}, ((getRequiredHeaders === null || getRequiredHeaders === void 0 ? void 0 : getRequiredHeaders()) || {})), ((getCustomRequiredHeaders === null || getCustomRequiredHeaders === void 0 ? void 0 : getCustomRequiredHeaders()) || {})), requestConfig.headers), requestConfig));
            if (this._defaultRestClientConfig.authorization) {
                const { refreshToken, getToken } = this._defaultRestClientConfig.authorization;
                const req = this.authorizationToken ? refreshToken : getToken;
                const data = req ? yield req() : undefined;
                if (data) {
                    this.authorizationToken = data;
                }
            }
            if (this.authorizationToken) {
                reqHeaders.set('authorization', `Bearer ${this.authorizationToken}`);
            }
            if (requestConfig.signal)
                return Object.assign(Object.assign({}, requestConfig), { headers: reqHeaders });
            const ac = new AbortController();
            const signal = ac.signal;
            abortControllersMap.set(signal, ac);
            return Object.assign(Object.assign({}, requestConfig), { headers: reqHeaders, signal });
        }), err => errorHandler({ err }));
        requester.interceptors.response.use(response => {
            var _a;
            if (((_a = response.config) === null || _a === void 0 ? void 0 : _a.signal) && abortControllersMap.has(response.config.signal)) {
                abortControllersMap.delete(response.config.signal);
            }
            const headers = (response.headers instanceof AxiosHeaders ? response.headers.toJSON() : response.headers);
            onResponse === null || onResponse === void 0 ? void 0 : onResponse(Object.assign(Object.assign({}, response), { headers }), onCustomResponse, skipErrorHandling);
            return Object.assign(Object.assign({}, response), { headers });
        }, err => errorHandler({ err, onError, onCustomError, skipErrorHandling }));
        return new RestClient({ requester, abortControllersMap, instanceConfig });
    }
}
